/**
 * Minimal toast system. Single container mounted on first use.
 * Uses GSAP for slide-in / slide-out.
 */
import { gsap } from 'gsap'

export type ToastKind = 'ok' | 'error' | 'info'

const ICONS: Record<ToastKind, string> = {
  ok: '✓',
  error: '✕',
  info: 'ℹ',
}

let container: HTMLDivElement | null = null

function ensureContainer(): HTMLDivElement {
  if (container) return container
  container = document.createElement('div')
  container.className = 'toast-container'
  container.setAttribute('aria-live', 'polite')
  container.setAttribute('aria-atomic', 'false')
  document.body.appendChild(container)
  return container
}

export function showToast(
  message: string,
  kind: ToastKind = 'info',
  durationMs = 4000,
): void {
  const host = ensureContainer()

  const el = document.createElement('div')
  el.className = `toast ${kind}`
  el.setAttribute('role', 'status')

  const icon = document.createElement('span')
  icon.setAttribute('aria-hidden', 'true')
  icon.textContent = ICONS[kind]
  icon.style.fontSize = '1.1rem'
  icon.style.fontWeight = '800'

  const text = document.createElement('span')
  text.textContent = message

  el.append(icon, text)
  host.appendChild(el)

  // Slide in from the right
  gsap.fromTo(
    el,
    { x: 420, opacity: 0 },
    { x: 0, opacity: 1, duration: 0.45, ease: 'power3.out' },
  )

  // Auto-dismiss
  gsap.to(el, {
    x: 420,
    opacity: 0,
    delay: durationMs / 1000,
    duration: 0.35,
    ease: 'power2.in',
    onComplete: () => {
      el.remove()
    },
  })
}
